import React from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SEO } from "@/lib/seo";
import { ArrowRight, Info, HeartPulse, Users, ShieldAlert, Phone } from "lucide-react";
import { PREP_GUIDES } from "@/data/prepGuides";
import { useLocalize } from "@/data/localize";
import { CLINIC_PHONE } from "@/lib/mailto";

export default function BowelPrepHubPage() {
    const { t } = useTranslation();
    const { localize } = useLocalize();
    const picoRaw = PREP_GUIDES.find((g) => g.slug === "pico-salax");
    const pegRaw = PREP_GUIDES.find((g) => g.slug === "peglite");
    const pico = picoRaw && localize(picoRaw);
    const peg = pegRaw && localize(pegRaw);

    return (
        <>
            <SEO title={t("bowelPrep.seoTitle")} description={t("bowelPrep.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs
                    items={[
                        { to: "/resources", label: t("resources.crumb") },
                        { label: t("bowelPrep.crumb") },
                    ]}
                />
            </div>

            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("bowelPrep.chip")}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {t("bowelPrep.title")}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {t("bowelPrep.intro")}
                    </p>
                </div>
            </section>

            <section className="container-page pb-2">
                <div
                    className="card-soft p-6 sm:p-7 grain"
                    data-testid="bowel-prep-which"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}
                >
                    <div className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25 shrink-0">
                            <Info className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] font-semibold">
                                {t("bowelPrep.whichChip")}
                            </p>
                            <h2 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-[color:var(--brand-navy)]">
                                {t("bowelPrep.whichTitle")}
                            </h2>
                            <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--brand-teal)]">
                                <li><Trans i18nKey="bowelPrep.which1" components={{ 1: <strong className="text-[color:var(--brand-navy)]" />, 3: <strong className="text-[color:var(--brand-navy)]" /> }} /></li>
                                <li><Trans i18nKey="bowelPrep.which2" components={{ 1: <strong className="text-[color:var(--brand-navy)]" />, 3: <strong className="text-[color:var(--brand-navy)]" /> }} /></li>
                                <li><Trans i18nKey="bowelPrep.which3" values={{ phone: CLINIC_PHONE }} components={{ 1: <a href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`} className="text-[color:var(--brand-teal-2)] font-semibold" /> }} /></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page section-y">
                <div className="grid gap-6 lg:grid-cols-2" data-testid="bowel-prep-options">
                    <PrepCard guide={pico} icon={Users} badge={t("bowelPrep.forMostPatients")} badgeTone="teal" testId="prep-card-pico-salax" openLabel={t("bowelPrep.openGuide", { product: pico?.product })} />
                    <PrepCard guide={peg} icon={HeartPulse} badge={t("bowelPrep.forKidneyHeart")} badgeTone="navy" testId="prep-card-peglite" openLabel={t("bowelPrep.openGuide", { product: peg?.product })} />
                </div>
            </section>

            <section className="container-page pb-10">
                <div
                    className="card-soft p-5 sm:p-6 flex items-start gap-3"
                    style={{ background: "rgba(180,83,9,0.06)" }}
                >
                    <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--warning)" }} />
                    <div>
                        <p className="font-semibold text-[color:var(--brand-navy)]">
                            {t("bowelPrep.safetyTitle")}
                        </p>
                        <p className="mt-1 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {t("bowelPrep.safetyBody")}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-[color:var(--brand-navy)] inline-flex items-center gap-1.5">
                            <Phone className="h-4 w-4" /> {CLINIC_PHONE}
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}

function PrepCard({ guide, icon: Icon, badge, badgeTone, testId, openLabel }) {
    if (!guide) return null;
    const bg =
        badgeTone === "navy"
            ? "linear-gradient(135deg, rgba(15,39,71,0.08) 0%, rgba(255,255,255,1) 60%)"
            : "linear-gradient(135deg, rgba(14,124,134,0.12) 0%, rgba(255,255,255,1) 60%)";
    const badgeCls =
        badgeTone === "navy"
            ? "bg-[color:var(--brand-navy)] text-white"
            : "bg-[color:var(--brand-teal-2)] text-white";

    return (
        <article className="card-soft p-6 sm:p-8 grain flex flex-col h-full" style={{ background: bg }} data-testid={testId}>
            <div className="flex items-center justify-between gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-[color:var(--border-default)] text-[color:var(--brand-teal-2)]">
                    <Icon className="h-5 w-5" />
                </span>
                <span className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${badgeCls}`}>
                    {badge}
                </span>
            </div>
            <h3 className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                {guide.product}
            </h3>
            <p className="mt-1 text-sm text-[color:var(--brand-teal-2)] font-medium">{guide.tagline}</p>
            <p className="mt-4 text-sm text-[color:var(--text-muted)] leading-relaxed">
                {guide.who_is_this_for}
            </p>
            <div className="mt-6 mt-auto pt-2">
                <Link
                    to={`/resources/bowel-prep-guide/${guide.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[color:var(--brand-navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-teal-2)]"
                    data-testid={`${testId}-cta`}
                >
                    {openLabel}
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </article>
    );
}
